import type { Data } from "@generated/data";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePage } from "@inertiajs/react";
import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SharedData } from "@/types";
import { urlFor } from "~/client";
import { useTransmit } from "~/hooks/use_transmit";
import { useTransmitChannel } from "~/hooks/use-transmit-channel";

export type MiniTask = Data.MiniTask;

/**
 * Evento realtime de mini-tarea (topic privado por usuario). El backend solo
 * manda el `mini_task_id` (no la tarea completa): el cliente refresca la lista y
 * resuelve la tarea por id. `kind` decide popup vs aviso vs refresco silencioso.
 * Ver `app/events/mini_task_broadcast.ts`.
 */
type MiniTaskBroadcastEvent =
  | "assigned"
  | "reassigned"
  | "started"
  | "completed"
  | "canceled"
  | "expired"
  | "escalated";
type MiniTaskBroadcastKind = "popup" | "notice" | "refresh";
type MiniTaskRealtimePayload = {
  event: MiniTaskBroadcastEvent;
  mini_task_id: number;
  ticket_id?: number;
  kind: MiniTaskBroadcastKind;
};

/**
 * Entrada para crear una mini-tarea desde el panel del header. El dueño se
 * discrimina por `ownerType`: para `team` se envía `teamId` (+ opcional
 * `chatConversationId` como contexto); para `channel` el canal ES el dueño, así
 * que basta `chatConversationId`.
 */
export type CreateMiniTaskInput = {
  ownerType: "team" | "channel";
  teamId?: string | null;
  chatConversationId?: string | number | null;
  assignedToUserId: string;
  title: string;
  minutes: number;
};

/** Popup encolado: id de la tarea + qué transición lo disparó. */
export type MiniTaskPopupItem = {
  miniTaskId: string;
  event: MiniTaskBroadcastEvent;
};

type MiniTasksContextValue = {
  miniTasks: MiniTask[];
  assignedToMe: MiniTask[];
  created: MiniTask[];
  pendingAssignedCount: number;
  /**
   * Conteo de mini-tareas VIVAS (PENDING/IN_PROGRESS) asignadas a mí, agrupadas
   * por dueño, para pintar el indicador en las filas del sidebar. La clave es el
   * id del dueño como string (bigint serializado): `teamId` para equipos,
   * `chatConversationId` para canales. `ownerType` ya separa ambos mapas, así que
   * una tarea cuenta en exactamente una fila (sin doble conteo).
   */
  pendingByTeam: Map<string, number>;
  pendingByChannel: Map<string, number>;
  isLoading: boolean;
  isFetching: boolean;
  dataUpdatedAt: number;
  isMutating: boolean;
  currentPopup: MiniTaskPopupItem | null;
  dismissPopup: () => void;
  refetchList: () => Promise<void>;
  getById: (miniTaskId: string) => MiniTask | undefined;
  start: (miniTaskId: string) => Promise<void>;
  complete: (miniTaskId: string) => Promise<void>;
  reassign: (miniTaskId: string, toUserId: string | number) => Promise<void>;
  cancel: (miniTaskId: string) => Promise<void>;
  create: (input: CreateMiniTaskInput) => Promise<MiniTask>;
};

const MINI_TASKS_QUERY_KEY = ["mini-tasks"] as const;

const MiniTasksContext = createContext<MiniTasksContextValue | null>(null);

type MiniTasksListResponse = { miniTasks: MiniTask[] };

const loadMiniTasks = async (signal?: AbortSignal): Promise<MiniTask[]> => {
  const response = await axios.get<MiniTasksListResponse>(urlFor("mini-tasks.index"), { signal });
  return response.data.miniTasks ?? [];
};

/**
 * Provider único (montado en el layout autenticado) que centraliza el estado de
 * mini-tareas: carga la lista (React Query, caché compartida con el centro y el
 * header), se suscribe al canal privado del usuario y mantiene la COLA de popups
 * de asignación/reasignación. Las mutaciones invalidan la lista; el realtime
 * solo aporta una pista para refrescar/abrir popup (la verdad ya está en DB).
 */
export function MiniTasksProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation("chat");
  const { auth } = usePage<SharedData>().props;
  const companyId = auth.company?.id ? Number(auth.company.id) : null;
  const currentUserId = auth.user?.id ? String(auth.user.id) : null;
  const queryClient = useQueryClient();

  const [popupQueue, setPopupQueue] = useState<MiniTaskPopupItem[]>([]);

  const enabled = companyId !== null && currentUserId !== null;

  const listQuery = useQuery({
    queryKey: MINI_TASKS_QUERY_KEY,
    queryFn: ({ signal }) => loadMiniTasks(signal),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: MINI_TASKS_QUERY_KEY });
  }, [queryClient]);

  // Forzar un refetch y resolver cuando se asiente. Lo usa el popup para
  // distinguir "todavía no cargó" de "ya no está en mi lista" (reasignada a
  // otro / cancelada) antes de descartar el popup encolado.
  const refetchList = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: MINI_TASKS_QUERY_KEY });
  }, [queryClient]);

  const topic = companyId && currentUserId ? `/${companyId}/user/${currentUserId}/mini-task` : null;

  const handleRealtime = useCallback(
    (payload: MiniTaskRealtimePayload) => {
      if (!payload || typeof payload.mini_task_id !== "number") {
        return;
      }

      // La verdad vive en DB: cualquier evento refresca la lista del centro.
      refresh();

      const miniTaskId = String(payload.mini_task_id);

      if (payload.kind === "popup") {
        setPopupQueue((queue) =>
          queue.some((item) => item.miniTaskId === miniTaskId)
            ? queue
            : [...queue, { miniTaskId, event: payload.event }],
        );
        return;
      }

      if (payload.kind === "notice") {
        toast.message(
          t(
            `mini_tasks.notice.${payload.event}`,
            t("mini_tasks.notice.default", "Una mini-tarea cambió de estado"),
          ),
        );
      }
    },
    [refresh, t],
  );

  useTransmitChannel<MiniTaskRealtimePayload>(topic, handleRealtime);

  // Los eventos emitidos mientras el SSE estuvo caído (deploy, sleep del laptop,
  // corte de red) se pierden para siempre: el broadcast es una pista efímera, no
  // una cola. Al reconectar se refresca la lista para resincronizar el badge del
  // sidebar y el centro con la verdad de la DB (mismo patrón que el reload de
  // mensajes del chat al reconectar). Los popups perdidos no se reconstruyen: un
  // F5 tampoco los muestra, y la tarea queda visible en badge + centro.
  const { onReconnect } = useTransmit();
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return onReconnect(() => refresh());
  }, [enabled, onReconnect, refresh]);

  const dismissPopup = useCallback(() => {
    setPopupQueue((queue) => queue.slice(1));
  }, []);

  // Feedback de error centralizado: una mutación puede fallar legítimamente (403
  // si el estado cambió, la mini-tarea ya venció, etc.). Cada `onError` muestra
  // un toast localizado y refresca la lista para resincronizar el estado real,
  // de modo que TODO call-site queda cubierto sin repetir manejo de errores.
  const notifyError = useCallback(
    (key: string, fallback: string) => {
      toast.error(t(key, fallback));
      refresh();
    },
    [t, refresh],
  );

  const startMutation = useMutation({
    mutationFn: (miniTaskId: string) =>
      axios.post(urlFor("mini-tasks.start", { miniTaskId: Number(miniTaskId) })),
    onSuccess: refresh,
    onError: () => notifyError("mini_tasks.errors.start", "No se pudo iniciar la mini-tarea"),
  });

  const completeMutation = useMutation({
    mutationFn: (miniTaskId: string) =>
      axios.post(urlFor("mini-tasks.complete", { miniTaskId: Number(miniTaskId) })),
    onSuccess: refresh,
    onError: () => notifyError("mini_tasks.errors.complete", "No se pudo completar la mini-tarea"),
  });

  const reassignMutation = useMutation({
    mutationFn: ({ miniTaskId, toUserId }: { miniTaskId: string; toUserId: string | number }) =>
      axios.post(urlFor("mini-tasks.reassign", { miniTaskId: Number(miniTaskId) }), {
        toUserId: Number(toUserId),
      }),
    onSuccess: refresh,
    onError: () => notifyError("mini_tasks.errors.reassign", "No se pudo reasignar la mini-tarea"),
  });

  const cancelMutation = useMutation({
    mutationFn: (miniTaskId: string) =>
      axios.delete(urlFor("mini-tasks.destroy", { miniTaskId: Number(miniTaskId) })),
    onSuccess: refresh,
    onError: () => notifyError("mini_tasks.errors.cancel", "No se pudo cancelar la mini-tarea"),
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateMiniTaskInput) => {
      // El body espeja `createMiniTaskValidator` (claves camelCase): siempre
      // `ownerType`; en `team` se manda `teamId` (+ opcional `chatConversationId`
      // de contexto); en `channel` solo `chatConversationId` (el canal es dueño).
      const ownerBody =
        input.ownerType === "channel"
          ? { ownerType: "channel", chatConversationId: Number(input.chatConversationId) }
          : {
              ownerType: "team",
              teamId: Number(input.teamId),
              ...(input.chatConversationId != null
                ? { chatConversationId: Number(input.chatConversationId) }
                : {}),
            };

      const response = await axios.post<{ miniTask: MiniTask }>(urlFor("mini-tasks.store"), {
        ...ownerBody,
        assignedToUserId: Number(input.assignedToUserId),
        title: input.title,
        minutes: input.minutes,
      });
      return response.data.miniTask;
    },
    onSuccess: refresh,
    onError: () => notifyError("mini_tasks.errors.create", "No se pudo crear la mini-tarea"),
  });

  const miniTasks = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const assignedToMe = useMemo(
    () => miniTasks.filter((task) => task.assignedTo?.id === currentUserId),
    [miniTasks, currentUserId],
  );
  const created = useMemo(
    () => miniTasks.filter((task) => task.createdBy?.id === currentUserId),
    [miniTasks, currentUserId],
  );
  // Cuenta las mini-tareas vivas asignadas a mí (PENDING o IN_PROGRESS): ambas
  // siguen requiriendo mi acción (empezarlas o completarlas), así que el badge
  // del centro debe seguir contándolas una vez iniciadas.
  const pendingAssignedCount = useMemo(
    () =>
      assignedToMe.filter((task) => task.status === "PENDING" || task.status === "IN_PROGRESS")
        .length,
    [assignedToMe],
  );

  // Mini-tareas vivas asignadas a mí (PENDING o IN_PROGRESS): la base de los
  // mapas por dueño que alimentan el indicador del sidebar.
  const liveAssignedToMe = useMemo(
    () => assignedToMe.filter((task) => task.status === "PENDING" || task.status === "IN_PROGRESS"),
    [assignedToMe],
  );
  const pendingByTeam = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of liveAssignedToMe) {
      if (task.ownerType === "team" && task.teamId) {
        counts.set(task.teamId, (counts.get(task.teamId) ?? 0) + 1);
      }
    }
    return counts;
  }, [liveAssignedToMe]);
  const pendingByChannel = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of liveAssignedToMe) {
      if (task.ownerType === "channel" && task.chatConversationId) {
        counts.set(task.chatConversationId, (counts.get(task.chatConversationId) ?? 0) + 1);
      }
    }
    return counts;
  }, [liveAssignedToMe]);

  const getById = useCallback(
    (miniTaskId: string) => miniTasks.find((task) => task.id === miniTaskId),
    [miniTasks],
  );

  const value = useMemo<MiniTasksContextValue>(
    () => ({
      miniTasks,
      assignedToMe,
      created,
      pendingAssignedCount,
      pendingByTeam,
      pendingByChannel,
      isLoading: listQuery.isLoading,
      isFetching: listQuery.isFetching,
      dataUpdatedAt: listQuery.dataUpdatedAt,
      isMutating:
        startMutation.isPending ||
        completeMutation.isPending ||
        reassignMutation.isPending ||
        cancelMutation.isPending ||
        createMutation.isPending,
      currentPopup: popupQueue[0] ?? null,
      dismissPopup,
      refetchList,
      getById,
      start: async (miniTaskId) => {
        await startMutation.mutateAsync(miniTaskId);
      },
      complete: async (miniTaskId) => {
        await completeMutation.mutateAsync(miniTaskId);
      },
      reassign: async (miniTaskId, toUserId) => {
        await reassignMutation.mutateAsync({ miniTaskId, toUserId });
      },
      cancel: async (miniTaskId) => {
        await cancelMutation.mutateAsync(miniTaskId);
      },
      create: (input) => createMutation.mutateAsync(input),
    }),
    [
      miniTasks,
      assignedToMe,
      created,
      pendingAssignedCount,
      pendingByTeam,
      pendingByChannel,
      listQuery.isLoading,
      listQuery.isFetching,
      listQuery.dataUpdatedAt,
      startMutation,
      completeMutation,
      reassignMutation,
      cancelMutation,
      createMutation,
      popupQueue,
      dismissPopup,
      refetchList,
      getById,
    ],
  );

  return <MiniTasksContext.Provider value={value}>{children}</MiniTasksContext.Provider>;
}

/**
 * Acceso al estado compartido de mini-tareas. Devuelve `null` si se usa fuera del
 * provider, para que componentes que pueden renderizarse fuera del workspace
 * (p. ej. settings) no revienten; los consumidores deben manejar el `null`.
 */
export function useMiniTasks(): MiniTasksContextValue | null {
  return useContext(MiniTasksContext);
}
